import type { User } from 'firebase/auth';
import { geohashForLocation } from 'geofire-common';
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { subscriptionService, userService } from '@/services';

export function useUserProfile(user: User | null) {
  const queryClient = useQueryClient();

  // Load profile once, then keep it updated from Firestore live updates (see subscribeToProfile below).
  // Without this, React Query can re-fetch stale data and briefly undo onboarding.
  const { data: profile, isLoading } = useQuery({
    queryKey: ['userProfile', user?.uid],
    queryFn: () => (user ? userService.getPublicProfile(user.uid) : null),
    enabled: !!user,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Keep profile in sync when Firestore changes (e.g. onboarding sets onboarded: true)
  useEffect(() => {
    if (!user) return;

    const unsubscribe = userService.subscribeToProfile(user.uid, updatedProfile => {
      queryClient.setQueryData(['userProfile', user.uid], updatedProfile);
    });

    return () => unsubscribe();
  }, [user, queryClient]);

  // `visible` is maintained server-side by the syncUserVisibility Firestore
  // trigger; the client only backfills `geohash` for older profiles.
  useEffect(() => {
    if (!user || !profile) return;
    if (typeof profile.latitude !== 'number' || typeof profile.longitude !== 'number') return;

    const expected = geohashForLocation([profile.latitude, profile.longitude]);
    if (profile.geohash !== expected) {
      void userService.updateProfile(user.uid, { geohash: expected });
    }
  }, [profile, user]);

  const hasAutoSynced = useRef(false);
  useEffect(() => {
    if (hasAutoSynced.current) return;
    if (!profile?.subscription?.active) return;

    const expiresAt = profile.subscription.expiresAt;
    if (!expiresAt) return;
    if (new Date(expiresAt).getTime() > Date.now()) return;

    hasAutoSynced.current = true;
    void subscriptionService.syncSubscription().catch(() => {});
  }, [profile]);

  return {
    profile,
    isLoading,
  };
}
